import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase, invokeFunction } from "@/integrations/supabase/client";

// Cast helper for tables not yet in generated types
const fromAny = (table: string) => (supabase as unknown as { from: (t: string) => ReturnType<typeof supabase.from> }).from(table);

export interface SkuMapping {
  id: string;
  organisation_id: string;
  primary_value: string;
  alias_value: string;
  primary_type: string;
  alias_type: string;
  created_by_email: string | null;
  created_at: string;
}

export function useSkuMappings(organisationId: string, sourceOrganisationId?: string) {
  return useQuery({
    queryKey: ["sku-mappings", organisationId, sourceOrganisationId || ""],
    queryFn: async () => {
      // Fetch clone org's own mappings
      const { data, error } = await fromAny("product_mappings")
        .select("*")
        .eq("organisation_id", organisationId);
      if (error) throw error;
      const ownMappings = (data as unknown as SkuMapping[]) || [];

      if (!sourceOrganisationId) return ownMappings;

      // Fetch source org mappings
      const { data: sourceData, error: sourceError } = await fromAny("product_mappings")
        .select("*")
        .eq("organisation_id", sourceOrganisationId);
      if (sourceError) throw sourceError;
      const sourceMappings = (sourceData as unknown as SkuMapping[]) || [];

      // Merge: clone's own mappings take priority (by alias_value key)
      const seen = new Set<string>();
      const merged: SkuMapping[] = [];
      for (const m of ownMappings) {
        seen.add(m.alias_value.toLowerCase());
        merged.push(m);
      }
      for (const m of sourceMappings) {
        if (!seen.has(m.alias_value.toLowerCase())) {
          merged.push(m);
        }
      }
      return merged;
    },
    enabled: !!organisationId,
  });
}

export function useAvailableSkus(organisationId: string, sourceType?: string, sourceOrganisationId?: string) {
  return useQuery({
    queryKey: ["available-skus", organisationId, sourceType || "all", sourceOrganisationId || ""],
    queryFn: async () => {
      // Use a Map keyed by lowercase to deduplicate case-insensitively.
      // The first casing seen wins (DB sources are added first, preserving original casing).
      const skuMap = new Map<string, string>();
      const addSku = (sku: string) => {
        const key = sku.toLowerCase().trim();
        if (!skuMap.has(key)) skuMap.set(key, sku);
      };

      // --- Source org SKUs first (so clone's own casing wins on collision) ---
      if (sourceOrganisationId) {
        const { data: sourceMappings } = await fromAny("product_mappings")
          .select("primary_value")
          .eq("organisation_id", sourceOrganisationId);
        sourceMappings?.forEach((m: unknown) => {
          const mapping = m as { primary_value: string };
          addSku(mapping.primary_value);
        });

        const { data: sourceProducts } = await supabase
          .from("products")
          .select("product")
          .eq("organisation_id", sourceOrganisationId);
        sourceProducts?.forEach((p: unknown) => {
          if (p && typeof p === 'object' && 'product' in p && typeof (p as { product: unknown }).product === 'string') {
            addSku((p as { product: string }).product);
          }
        });

        try {
          const { data } = await invokeFunction(supabase, 
            "fetch-organisation-skus",
            { body: { organisationId: sourceOrganisationId, limit: 5000, sources: [], sourceType: sourceType || "" } }
          );
          data?.skus?.forEach((s: string) => addSku(s));
        } catch {
          // non-critical
        }
      }

      // --- Clone/own org SKUs (overwrites source casing on collision) ---
      // Re-create map entries for own org so own casing wins
      const ownSkuMap = new Map<string, string>();
      const addOwnSku = (sku: string) => {
        const key = sku.toLowerCase().trim();
        ownSkuMap.set(key, sku);
      };

      // Source 1: Primary values from product_mappings
      const { data: mappings } = await fromAny("product_mappings")
        .select("primary_value")
        .eq("organisation_id", organisationId);
      mappings?.forEach((m: unknown) => {
        const mapping = m as { primary_value: string };
        addOwnSku(mapping.primary_value);
      });

      // Source 2: Products table
      const { data: products } = await supabase
        .from("products")
        .select("product")
        .eq("organisation_id", organisationId);
      products?.forEach((p: unknown) => {
        if (p && typeof p === 'object' && 'product' in p && typeof (p as { product: unknown }).product === 'string') {
          addOwnSku((p as { product: string }).product);
        }
      });

      // Source 3: Data source SKUs via edge function
      try {
        const { data } = await invokeFunction(supabase, 
          "fetch-organisation-skus",
          { body: { organisationId, limit: 5000, sources: [], sourceType: sourceType || "" } }
        );
        data?.skus?.forEach((s: string) => addOwnSku(s));
      } catch {
        // non-critical
      }

      // Merge: own org overwrites source org casing
      for (const [key, val] of ownSkuMap) {
        skuMap.set(key, val);
      }

      return Array.from(skuMap.values()).sort();
    },
    enabled: !!organisationId,
  });
}

export function useCreateSkuMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      organisationId: string;
      primarySku: string;
      aliasSku: string;
      createdByEmail: string;
      productIdentifier?: string;
      aliasType?: string;
    }) => {
      const productIdentifier = params.productIdentifier ?? "SKU";
      const { data, error } = await fromAny("product_mappings")
        .insert({
          organisation_id: params.organisationId,
          primary_value: params.primarySku,
          alias_value: params.aliasSku,
          primary_type: productIdentifier,
          alias_type: params.aliasType ?? productIdentifier,
          created_by_email: params.createdByEmail,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({
        queryKey: ["sku-mappings", params.organisationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["available-skus", params.organisationId],
      });
    },
  });
}
